import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import useAsync, { AsyncState } from 'react-use/lib/useAsync';
import {
  CustomPresentationRetrieverApiRef,
  wbSafeNunjucksApiRef,
} from '../../apis';
import { ReactElement, useEffect, useMemo } from 'react';
import {
  CustomViewPages,
  extractNunjucksTemplates,
  extractIncludeFromCode,
  resolveIncludesInJsx,
} from './';
import { buildJsx } from './jsxSerializer';
import { CustomViewComponent } from './CustomViewComponent';

type UseCustomPresentation = {
  jsx: ReactElement;
  defaultActions: Record<string, Function>;
};

export function useCustomPresentation(
  id: string,
  typeId: string = '',
  templateId: string = '',
): AsyncState<UseCustomPresentation> {
  const CPRetriever = useApi(CustomPresentationRetrieverApiRef);
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);

  const experimentalNunjucksRendering =
    useApi(configApiRef).getOptionalBoolean(
      'mesh.experimentalNunjucksRendering',
    ) ?? true;

  const defaultViewJsx = CustomViewPages.get(id);

  if (!defaultViewJsx)
    throw new Error(`Could not find custom view with id ${id}`);

  // Extract includes from default view to fetch their customizations as well
  const included = useMemo(
    () => extractIncludeFromCode(defaultViewJsx, typeId, templateId),
    [defaultViewJsx, typeId, templateId],
  );

  const customPresentation = useAsync(async () => {
    const viewsIds = [{ id, typeId, templateId }].concat(included);
    // fetches customizations for main view and included views
    // TODO this means customizations can only include other views that are also included in the default view
    const views = await Promise.all(
      viewsIds.map(async inc => ({
        id: inc.id,
        typeId: inc.typeId,
        templateId: inc.templateId,
        code: await CPRetriever.retrieve(inc.id, inc.typeId, inc.templateId),
      })),
    );

    const mainCustomization = views[0];
    const includedCustomizations = views.slice(1);

    const mainCustomizationJsx = buildJsx(
      CustomViewComponent,
      mainCustomization.code,
    );

    const viewJsx = mainCustomizationJsx ?? defaultViewJsx;

    const getInclude = (
      incId: string,
      incTypeId: string,
      incTemplateId: string,
    ) => {
      const builtJsx = buildJsx(
        CustomViewComponent,
        (
          includedCustomizations.find(
            e =>
              e.id === incId &&
              e.typeId === incTypeId &&
              e.templateId === incTemplateId,
          ) || {}
        ).code,
      );

      if (builtJsx) return builtJsx;
      // fallback to default page if no customizations are found
      return CustomViewPages.get(incId);
    };

    let cleanupFn;

    // failsafe mechanism to disable new nunjucks rendering flow if something goes wrong
    if (experimentalNunjucksRendering) {
      // get jsx with includes resolved to get all nunjucks templates that will be used
      const resolvedJsx = resolveIncludesInJsx(viewJsx, {
        typeId,
        templateId,
        includeResolver: getInclude,
      });

      const dynamicExpressions = extractNunjucksTemplates(resolvedJsx);

      // mark loaded templates as no longer needed when unmounting
      cleanupFn = nunjucksApi.load([...dynamicExpressions]);
    }

    return {
      jsx: viewJsx,
      defaultActions: {
        getCustomViewInfo: () => ({ id, typeId, templateId }),
        getInclude,
      },
      cleanupFn,
    };
  }, [id, typeId, templateId, defaultViewJsx]);

  // executes cleanup function on unmount
  useEffect(() => {
    return () => {
      if (customPresentation.value?.cleanupFn) {
        customPresentation.value.cleanupFn();
      }
    };
  }, [customPresentation.value]);

  return customPresentation;
}
